import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../axios/axios";

const initialState = {
  title: "People Behind",
  description: "",
  images: [],
  status: "idle",
  error: null,
  peopleBehind: [],
};

// Fetch People Behind Data
export const fetchPeopleBehind = createAsyncThunk(
  "peopleBehind/fetchPeopleBehind",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/people-behind");
      //   console.log("response:", response.data.peopleBehind);
      return response.data.peopleBehind; // ✅ Correct key
    } catch (error) {
      console.error("Error fetching People Behind data:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create People Behind
export const createPeopleBehind = createAsyncThunk(
  "peopleBehind/createPeopleBehind",
  async (peopleBehindData, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append("name", peopleBehindData.name || "");
      formData.append("post", peopleBehindData.post || "");
      formData.append("location", peopleBehindData.location || "");

      // Only append images if images is an array and has items
      if (Array.isArray(peopleBehindData.images)) {
        peopleBehindData.images.forEach((image) => {
          if (image instanceof File) {
            formData.append("images", image);
          }
        });
      }
      const response = await api.post("/people-behind", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      dispatch(fetchPeopleBehind());
      return response.data.peopleBehind;
    } catch (error) {
      console.error("Error creating People Behind:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update People Behind
export const updatePeopleBehind = createAsyncThunk(
  "peopleBehind/updatePeopleBehind",
  async ({ id, peopleBehindData }, { rejectWithValue, dispatch }) => {
    console.log("Updating People Behind with ID:", id, peopleBehindData);

    try {
      const formData = new FormData();
      formData.append("name", peopleBehindData.name || "");
      formData.append("post", peopleBehindData.post || "");
      formData.append("location", peopleBehindData.location || "");

      // Only append images if images is an array and has items
      if (Array.isArray(peopleBehindData.images)) {
        peopleBehindData.images.forEach((image) => {
          if (image instanceof File) {
            formData.append("images", image);
          }
        });
      }
      const response = await api.patch(
        `/people-behind/update/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      dispatch(fetchPeopleBehind());
      return response.data.peopleBehind;
    } catch (error) {
      console.error("Error updating People Behind:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete People Behind
export const deletePeopleBehind = createAsyncThunk(
  "peopleBehind/deletePeopleBehind",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/people-behind/delete/${id}`);
      dispatch(fetchPeopleBehind()); // ✅ Re-fetch list after deletion
      return id;
    } catch (error) {
      console.error("Error deleting People Behind:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const peopleBehindSlice = createSlice({
  name: "peopleBehind",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPeopleBehind.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPeopleBehind.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.peopleBehind = Array.isArray(action.payload)
          ? action.payload
          : [action.payload]; // ✅ Handle both array and single object
      })
      .addCase(fetchPeopleBehind.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(createPeopleBehind.fulfilled, (state, action) => {
        // ✅ Add the new item directly if API returns it
        if (action.payload) {
          state.peopleBehind.push(action.payload);
        }
      })
      .addCase(updatePeopleBehind.fulfilled, (state, action) => {
        // ✅ Update the item in the array
        const index = state.peopleBehind.findIndex(
          (person) => person._id === action.payload._id
        );
        if (index !== -1) {
          state.peopleBehind[index] = action.payload;
        }
      })
      .addCase(deletePeopleBehind.fulfilled, (state, action) => {
        // ✅ Remove the deleted item
        state.peopleBehind = state.peopleBehind.filter(
          (person) => person._id !== action.payload
        );
      });
  },
});

export default peopleBehindSlice.reducer;
